/**
 * `ForcePasswordChangePage` — full-screen gate for `user.mustChangePassword`.
 *
 * WIRING THIS PAGE IN IS NOT DONE HERE (recommendation, not an edit)
 * -------------------------------------------------------------------
 * `routes.tsx` and `AuthProvider` are out of scope for this change (see the
 * wave brief), so the gate itself — checking `user?.mustChangePassword` on
 * every authenticated route and rendering this page instead of the
 * requested one until it clears — is not wired anywhere yet. The natural
 * home is beside the existing `status === "loading"` / permissions checks in
 * whatever component guards the authenticated route tree (`route-guard.ts`
 * per the CLAUDE.md convention, or the top of the router's authenticated
 * layout) — same shape as those checks, one more boolean. `MobileUser.
 * mustChangePassword` (`lib/auth/types.ts`) and `requiresPasswordChange()`
 * (`lib/auth/session.ts`) are already there to read.
 *
 * NO WAY PAST, BY DESIGN
 * -----------------------
 * No nav chrome, no cancel button, no link elsewhere. The only exit is a
 * successful password change, which signs the browser out and returns to
 * `/login` — the person must reauthenticate with the NEW password, which is
 * also the simplest possible proof to this client that the mandatory change
 * actually took: there is no local flag to flip and hope the next fetch
 * agrees, only a fresh, real sign-in.
 */
import { useNavigate } from "react-router-dom";
import { Wallet } from "lucide-react";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { ReactNode } from "react";
import { useSyncExternalStore } from "react";
import { onPasswordGate, passwordGateTripped } from "@/lib/api/client";
import { ChangePasswordForm } from "@/pages/security/ChangePasswordForm";

export function ForcePasswordChangePage() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  async function handleSuccess() {
    await logout();
    navigate("/login?passwordChanged=1", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--cp-canvas)] p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center gap-2">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
            style={{ background: "linear-gradient(135deg, var(--cp-accent), var(--cp-accent-2))" }}
          >
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xl font-bold text-[var(--cp-ink)]">AIM</div>
          </div>
        </div>

        <div className="cp-card p-6">
          <h1 className="mb-1 text-lg font-semibold text-[var(--cp-ink)]">
            You need to change your password
          </h1>
          <p className="mb-6 text-[12.5px] text-[var(--cp-ink-3)]">
            An admin reset your password, or it's time for a scheduled change. Choose a new one to
            continue — there's no way to skip this.
          </p>

          <ChangePasswordForm onSuccess={() => void handleSuccess()} submitLabel="Change password and continue" />
        </div>
      </div>
    </div>
  );
}


/**
 * Route-level gate (wave-3 integration): renders the forced-change screen
 * INSTEAD of the app while the signed-in user carries `mustChangePassword`.
 * Sits inside `RequireAuth`, so `user` is resolved; the server enforces the
 * same rule globally (`global_password_change_gate`), this is the UX half.
 */
export function ForcePasswordChangeGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Two triggers: the flag on a fresh session, OR the server's 403 on a stale
  // one (see lib/api/client.ts — sessions issued before the migration carry no
  // flag, and the first gated response is how the client learns).
  const tripped = useSyncExternalStore(onPasswordGate, passwordGateTripped, () => false);
  if (user?.mustChangePassword || tripped) return <ForcePasswordChangePage />;
  return <>{children}</>;
}
